import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Clock } from '../../icons/wp-icons';
import { zeiterfassungApi } from '../../api/zeiterfassungApi';
import apiClient from '../../api/apiClient';
import Modal from '../Common/Modal';

function ManuelleEingabe({ terminId, onClose, onSuccess }) {
  const [katalogPositionen, setKatalogPositionen] = useState([]);
  const [formData, setFormData] = useState({
    katalogPositionId: '',
    datum: new Date().toISOString().split('T')[0],
    startzeit: '09:00',
    endzeit: '10:00',
    notiz: ''
  });
  const [pausen, setPausen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Katalogpositionen laden
  useEffect(() => {
    const loadKatalog = async () => {
      try {
        const response = await apiClient.get('/katalog', {
          params: { einheit: 'Std', aktiv: true }
        });
        const stdPositionen = (response.data || []).filter(
          pos => pos.einheit === 'Std' && pos.aktiv
        );
        setKatalogPositionen(stdPositionen);
        if (stdPositionen.length > 0) {
          setFormData(prev => ({ ...prev, katalogPositionId: stdPositionen[0].id }));
        }
      } catch (err) {
        console.error('Fehler beim Laden des Katalogs:', err);
      }
    };
    loadKatalog();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addPause = () => {
    setPausen(prev => [...prev, { startzeit: '12:00', endzeit: '12:30' }]);
  };

  const removePause = (index) => {
    setPausen(prev => prev.filter((_, i) => i !== index));
  };

  const updatePause = (index, field, value) => {
    setPausen(prev => prev.map((pause, i) =>
      i === index ? { ...pause, [field]: value } : pause
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!formData.katalogPositionId) {
      setError('Bitte wähle eine Tätigkeit aus');
      return;
    }

    if (!formData.datum || !formData.startzeit || !formData.endzeit) {
      setError('Datum, Start- und Endzeit sind erforderlich');
      return;
    }

    // Startzeit muss vor Endzeit sein
    if (formData.startzeit >= formData.endzeit) {
      setError('Startzeit muss vor Endzeit liegen');
      return;
    }

    // Pausen validieren
    for (const pause of pausen) {
      if (pause.startzeit >= pause.endzeit) {
        setError('Pausenstart muss vor Pausenende liegen');
        return;
      }
      if (pause.startzeit < formData.startzeit || pause.endzeit > formData.endzeit) {
        setError('Pausen müssen innerhalb der Arbeitszeit liegen');
        return;
      }
    }

    setLoading(true);

    try {
      // DateTime-Strings erstellen
      const startzeit = new Date(`${formData.datum}T${formData.startzeit}:00`);
      const endzeit = new Date(`${formData.datum}T${formData.endzeit}:00`);

      // Pausen als DateTime-Objekte
      const pausenFormatiert = pausen.map(pause => ({
        start: new Date(`${formData.datum}T${pause.startzeit}:00`).toISOString(),
        ende: new Date(`${formData.datum}T${pause.endzeit}:00`).toISOString()
      }));

      await zeiterfassungApi.createManual({
        terminId,
        katalogPositionId: formData.katalogPositionId,
        startzeit: startzeit.toISOString(),
        endzeit: endzeit.toISOString(),
        pausen: pausenFormatiert,
        notiz: formData.notiz || null
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  // Stundensatz der ausgewählten Position
  const getStundensatz = () => {
    const pos = katalogPositionen.find(p => p.id === formData.katalogPositionId);
    return pos ? pos.verkaufspreis : 0;
  };

  // Arbeitszeit berechnen (für Vorschau)
  const berechneArbeitszeit = () => {
    if (!formData.startzeit || !formData.endzeit) return 0;

    const start = new Date(`2000-01-01T${formData.startzeit}:00`);
    const ende = new Date(`2000-01-01T${formData.endzeit}:00`);
    let gesamtMs = ende - start;

    // Pausen abziehen
    for (const pause of pausen) {
      if (pause.startzeit && pause.endzeit) {
        const pauseStart = new Date(`2000-01-01T${pause.startzeit}:00`);
        const pauseEnde = new Date(`2000-01-01T${pause.endzeit}:00`);
        gesamtMs -= (pauseEnde - pauseStart);
      }
    }

    return Math.max(0, Math.floor(gesamtMs / (1000 * 60)));
  };

  const formatMinuten = (minuten) => {
    if (minuten <= 0) return '0 Min';
    const stunden = Math.floor(minuten / 60);
    const restMinuten = minuten % 60;
    if (stunden === 0) return `${restMinuten} Min`;
    if (restMinuten === 0) return `${stunden} Std`;
    return `${stunden} Std ${restMinuten} Min`;
  };

  const arbeitsminuten = berechneArbeitszeit();
  const voraussichtlicheKosten = (arbeitsminuten / 60) * getStundensatz();

  return (
    <Modal isOpen={true} onClose={onClose} title="Arbeitszeit manuell eintragen">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Tätigkeit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tätigkeit *
          </label>
          <select
            name="katalogPositionId"
            value={formData.katalogPositionId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          >
            <option value="">-- Tätigkeit wählen --</option>
            {katalogPositionen.map((pos) => (
              <option key={pos.id} value={pos.id}>
                {pos.bezeichnung} ({pos.verkaufspreis.toFixed(2)} €/Std)
              </option>
            ))}
          </select>
        </div>

        {/* Datum */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Datum *
          </label>
          <input
            type="date"
            name="datum"
            value={formData.datum}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
        </div>

        {/* Start- und Endzeit */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Von *
            </label>
            <input
              type="time"
              name="startzeit"
              value={formData.startzeit}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bis *
            </label>
            <input
              type="time"
              name="endzeit"
              value={formData.endzeit}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>
        </div>

        {/* Pausen */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Pausen
            </label>
            <button
              type="button"
              onClick={addPause}
              className="flex items-center gap-1 text-sm text-primary hover:text-primary-dark"
            >
              <Plus className="w-4 h-4" />
              Pause hinzufügen
            </button>
          </div>

          {pausen.length > 0 && (
            <div className="space-y-2">
              {pausen.map((pause, index) => (
                <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                  <input
                    type="time"
                    value={pause.startzeit}
                    onChange={(e) => updatePause(index, 'startzeit', e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <span className="text-gray-400">bis</span>
                  <input
                    type="time"
                    value={pause.endzeit}
                    onChange={(e) => updatePause(index, 'endzeit', e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removePause(index)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notiz */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notiz (optional)
          </label>
          <textarea
            name="notiz"
            value={formData.notiz}
            onChange={handleChange}
            rows={2}
            placeholder="z.B. Fensterreparatur Küche"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Vorschau */}
        {formData.katalogPositionId && arbeitsminuten > 0 && (
          <div className="bg-primary-light rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-primary" />
                  {formatMinuten(arbeitsminuten)}
                </span>
                <span className="text-gray-500">
                  × {getStundensatz().toFixed(2)} €/Std
                </span>
              </div>
              <span className="font-medium text-primary">
                = {voraussichtlicheKosten.toFixed(2)} €
              </span>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-primary text-white py-2 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
          >
            {loading ? 'Speichern...' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-button font-medium hover:bg-gray-300 transition"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ManuelleEingabe;
